import { 
  sendEmailVerification,
  updateEmail,
  verifyBeforeUpdateEmail,
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendPasswordResetEmail
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../config/firebase";

// Use the pre-configured functions instance from firebase.js which includes the region
console.log("Using Firebase Functions with region:", functions);
const sendPasswordResetFunction = httpsCallable(functions, "sendPasswordReset");
const sendEmailChangeConfirmationFunction = httpsCallable(functions, "sendEmailChangeConfirmation");

export const sendVerificationEmail = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error("No user is currently signed in");
  
  // Firebase will trigger our custom email through the onCreate function
  await sendEmailVerification(user);
  return true;
};

export const initiatePasswordReset = async (email) => {
  try {
    if (!email) {
      console.error("Email is required but was empty or undefined");
      throw new Error("Email is required");
    }

    // Verify email format with a regular expression
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      console.error("Invalid email format:", email);
      throw new Error("Invalid email format");
    }
    
    console.log("Initiating password reset for:", email);
    
    // Use our custom Firebase function instead of Firebase's built-in function
    const result = await sendPasswordResetFunction({ email: email.trim() });
    
    console.log("Custom password reset email sent successfully");
    return result.data.success;
  } catch (error) {
    console.error("Password reset error:", error);
    console.error("Error details:", {
      code: error.code,
      message: error.message,
      details: error.details,
      stack: error.stack
    });
    
    // Handle specific error cases
    if (error.code === "auth/invalid-email") {
      throw new Error("Please enter a valid email address");
    }
    
    if (error.code === "auth/user-not-found") {
      throw new Error("No account found with this email address");
    }
    
    if (error.code === "auth/too-many-requests") {
      throw new Error("Too many password reset attempts. Please try again later.");
    }
    
    // If it's a Firebase Auth error, it will have a message
    if (error.message) {
      throw new Error(error.message);
    }
    
    throw new Error("Failed to send password reset email. Please try again.");
  }
};

// Test function to debug the password reset functionality
export const testPasswordReset = async (email) => {
  try {
    console.log("Test password reset with email:", email);
    
    // Test directly calling the function with various formats to see which one works
    const formats = [
      { email },
      { email: email },
      { data: { email } },
      { data: email },
    ];
    
    for (let i = 0; i < formats.length; i++) {
      const format = formats[i];
      console.log(`Testing format ${i + 1}:`, format);
      try {
        const result = await sendPasswordResetFunction(format);
        console.log(`Format ${i + 1} result:`, result);
        return { success: true, format: i + 1, result };
      } catch (error) {
        console.error(`Format ${i + 1} error:`, error);
      }
    }
    
    throw new Error("All test formats failed");
  } catch (error) {
    console.error("Test password reset error:", error);
    throw error;
  }
};

export const changeEmailAddress = async (newEmail, currentPassword) => {
  const user = auth.currentUser;
  if (!user) throw new Error("No user is currently signed in");
  
  // Re-authenticate user before email change
  const credential = EmailAuthProvider.credential(user.email, currentPassword);
  await reauthenticateWithCredential(user, credential);
  
  // Send verification to new email using our custom function
  const result = await sendEmailChangeConfirmationFunction({ newEmail });
  
  if (!result.data?.success) {
    throw new Error("Failed to initiate email change");
  }
  
  return true;
};

export const isEmailVerified = () => {
  const user = auth.currentUser;
  return user?.emailVerified ?? false;
};

export const reauthenticateUser = async (password) => {
  const user = auth.currentUser;
  if (!user) throw new Error("No user is currently signed in");
  
  const credential = EmailAuthProvider.credential(user.email, password);
  await reauthenticateWithCredential(user, credential);
  return true;
};

// Update user email in Firebase Auth (admin function)
export const updateUserEmail = async (userId, newEmail) => {
  try {
    if (!userId || !newEmail) {
      throw new Error("User ID and email are required");
    }

    // Verify email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      throw new Error("Invalid email format");
    }

    console.log("Updating user email:", userId, "to:", newEmail);

    // Get the current user's ID token for authentication
    const user = auth.currentUser;
    if (!user) {
      throw new Error("No user is currently signed in");
    }

    const idToken = await user.getIdToken();

    // Call the Firebase function to update the user's email
    const { getCloudFunctionUrl } = await import('../config/cloudFunctions');
    const updateEmailUrl = process.env.REACT_APP_CLOUD_FUNCTION_UPDATE_USER_EMAIL || getCloudFunctionUrl('updateUserEmail');
    const response = await fetch(updateEmailUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({
        userId: userId,
        newEmail: newEmail
      })
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Failed to update user email');
    }

    if (!result.success) {
      throw new Error(result.message || 'Failed to update user email');
    }

    console.log("User email updated successfully");
    return true;
  } catch (error) {
    console.error("Error updating user email:", error);
    throw error;
  }
};
